import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Button } from "src/components/base/button";
import HeadingLayout from "src/components/base/heading-layout";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import React, { Suspense } from "react";
import { ApiInternal } from "src/api/api_internal";
import { Dialog, DialogActions, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { SimpleAccount } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link AccountsOverview}
 */
export type AccountsOverviewProps = {};

/**
 * An overview over all devices
 */
function AccountsOverview(props: AccountsOverviewProps) {
    const [t] = useTranslation("admin-accounts-overview");

    const router = useRouter();
    const data = Route.useLoaderData();

    const [deleteAccount, setDeleteAccount] = React.useState<SimpleAccount>();

    /**
     * Api call to delete Account
     */
    const deleteAcc = async () => {
        if (!deleteAccount) {
            return;
        }

        await ApiInternal.accounts.delete(deleteAccount.uuid);
        await router.invalidate({ sync: true });
        setDeleteAccount(undefined);
    };

    return (
        <>
            <HeadingLayout heading={t("heading.acc-mgmt")}>
                <Table className={"text-zinc-800 dark:text-zinc-200"} dense={true}>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{t("label.name")}</TableHeader>
                            <TableHeader>{t("label.mail")}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((account) => (
                            <TableRow key={account.uuid}>
                                <TableCell>{account.display_name}</TableCell>
                                <TableCell>{account.preferred_username}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </HeadingLayout>

            {deleteAccount !== undefined && (
                <Suspense>
                    <Dialog open={true} onClose={() => setDeleteAccount(undefined)}>
                        <Form onSubmit={deleteAcc}>
                            <DialogTitle>{t("heading.delete-acc", { name: deleteAccount.display_name })}</DialogTitle>
                            <DialogActions>
                                <Button onClick={() => setDeleteAccount(undefined)}>{t("button.no")}</Button>
                                <Button type={"submit"} color={"red"}>
                                    {t("button.yes")}
                                </Button>
                            </DialogActions>
                        </Form>
                    </Dialog>
                </Suspense>
            )}
        </>
    );
}

export const Route = createFileRoute("/_admin/a/accounts/")({
    component: AccountsOverview,
    loader: async () =>
        await ApiInternal.accounts
            .getAll()
            .then((res) => res.list.sort((a, b) => a.display_name.localeCompare(b.display_name))),
});
